import {INDRIVE_ID_PARENT_ORIGIN} from '@shared/utils/indriveMessages';
import Loader from '@shared/components/Loader';
import {RootState} from '@store';
import {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {useLocation} from 'react-router';
import {I18nextProvider} from 'react-i18next';
import {
  getCountryCodeFromUrl,
  getDeploymentStatus,
  getThemeFromApi,
} from '@shared/api/api';
import Maintenance from '@pages/Maintenance';
import {LANGUAGES} from '@common/constants';
import {DefaultTheme, ThemeProvider} from 'styled-components';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import i18n from './i18n';
import AppContainer from './App.style';
import AppRoutes from './routes';
import Config from './config/themeConfig.json';

export default function App() {
  const {pathname} = useLocation();
  const [isMaintenance, setIsMaintenance] = useState<boolean | null>(false);

  useEffect(() => {
    const handleLanguageMessage = (event: MessageEvent) => {
      if (window.parent === window || event.source !== window.parent) return;

      if (
        getCountryCodeFromUrl() === 'ID' &&
        event.origin !== INDRIVE_ID_PARENT_ORIGIN
      )
        return;

      const message = event.data;
      if (!message || message.type !== 'SET_LANGUAGE') return;

      const language = message.payload?.language;
      if (
        typeof language !== 'string' ||
        !LANGUAGES.some((item) => item.value === language)
      ) {
        return;
      }

      i18n.changeLanguage(language);
    };

    window.addEventListener('message', handleLanguageMessage);
    return () => {
      window.removeEventListener('message', handleLanguageMessage);
    };
  }, []);

  useEffect(() => {
    document.body.style.cssText = 'zoom: 100%';

    // Clean up the style on component unmount
    return () => {
      document.body.style.cssText = '';
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    const checkMaintenance = async () => {
      const status = await getDeploymentStatus();
      setIsMaintenance(status.data?.isInMaintenance);
    };

    checkMaintenance();
  }, []);

  const isAppLoading = useSelector(
    (state: RootState) => state.loader.isAppLoading
  );

  const reduxTheme = useSelector((state: RootState) => state.login.theme);
  const [currentTheme, setCurrentTheme] = useState<DefaultTheme>(
    Config[0].theme
  );
  const {hostname} = window.location;

  useEffect(() => {
    const loadTheme = () => {
      let themeName = '';
      if (hostname.startsWith('boost')) {
        if (reduxTheme) {
          themeName = reduxTheme;
        } else {
          themeName = 'boost';
        }
      } else {
        themeName = reduxTheme;
      }
      const themeConfig = Config.find((c) => c.name === themeName) || Config[0];
      setCurrentTheme(themeConfig.theme as DefaultTheme);
    };
    loadTheme();
  }, [reduxTheme]);

  return (
    <ThemeProvider theme={currentTheme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <AppContainer>
          <I18nextProvider i18n={i18n}>
            {isMaintenance ? (
              <Maintenance />
            ) : (
              <>
                <AppRoutes />
                {isAppLoading && <Loader />}
              </>
            )}
          </I18nextProvider>
        </AppContainer>
      </LocalizationProvider>
    </ThemeProvider>
  );
}
